import { useState, useCallback, useContext } from "react";
import { Select, MenuItem, TextField, FormControl } from "@mui/material";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import colors from "assets/theme-dark/base/colors";
import { styled } from "@mui/material/styles";
import { Dispatch, SetStateAction } from "react";
import { useMatcherTableDataLazyQuery } from "../../../../graphql/types/graphql";
import { enqueueSnackbar } from "notistack";
import { context } from "utils/Provider";

interface TableRow {
  FX_UNIQUE_ID: string;
  [key: string]: unknown;
}

interface TableColumn {
  Header: string;
  accessor: string;
}

interface TableData {
  columns: TableColumn[];
  rows: TableRow[];
}

interface KeywordSearchProps {
  tableName: string;
  tableData: TableData;
  pageSize: number;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  setTableData: Dispatch<SetStateAction<TableRow[]>>;
  setTotalCount: (count: number) => void;
  setHasSearched: (hasSearched: boolean) => void;
  searchTerm?: string;
  setSearchTerm?: (value: string) => void;
  selectedColumn?: string;
  setSelectedColumn?: (value: string) => void;
  resetSearch?: () => void;
}

const KeywordSearch = ({
  tableName,
  tableData,
  pageSize,
  currentPage,
  setCurrentPage,
  setTableData,
  setTotalCount,
  setHasSearched,
  searchTerm: controlledSearchTerm,
  setSearchTerm: setControlledSearchTerm,
  selectedColumn: controlledSelectedColumn,
  setSelectedColumn: setControlledSelectedColumn,
  resetSearch: controlledResetSearch,
}: KeywordSearchProps) => {
  const { selectedCube } = useContext(context);
  const [internalSelectedColumn, setInternalSelectedColumn] = useState<string>("");
  const [internalKeywordInput, setInternalKeywordInput] = useState<string>("");

  const selectedColumn = controlledSelectedColumn !== undefined ? controlledSelectedColumn : internalSelectedColumn;
  const keywordInput = controlledSearchTerm !== undefined ? controlledSearchTerm : internalKeywordInput;
  const setSelectedColumn = setControlledSelectedColumn || setInternalSelectedColumn;
  const setKeywordInput = setControlledSearchTerm || setInternalKeywordInput;

  const [executeQuery, { loading, error }] = useMatcherTableDataLazyQuery({
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      if (data?.matcherTableData) {
        const rows = data.matcherTableData.edges.map((edge) => ({
          ...edge,
        })) as TableRow[];

        setTableData(rows);
        setTotalCount(data.matcherTableData.totalCount);
        setCurrentPage(0);
        setHasSearched(true);
      }
    },
    onError: (error) => {
      enqueueSnackbar(`Error fetching table data: ${error instanceof Error ? error.message : "Something went wrong"}`, {
        variant: "error",
        autoHideDuration: 5000,
      });
    },
  });

  const handleSearch = useCallback(() => {
    if (!selectedColumn || !keywordInput) return;

    executeQuery({
      variables: {
        tableName,
        cubeId: selectedCube.cubeId,
        limit: pageSize,
        offset: currentPage * pageSize,
        columnName: selectedColumn,
        keyword: keywordInput,
      },
    });
  }, [selectedColumn, keywordInput, executeQuery, tableName, selectedCube.cubeId, pageSize, currentPage]);

  const handleReset = useCallback(() => {
    if (controlledResetSearch) {
      controlledResetSearch();
      return;
    }

    setSelectedColumn("");
    setKeywordInput("");
    setTableData(tableData.rows);
    setTotalCount(tableData.rows.length);
    setCurrentPage(0);
    setHasSearched(false);
  }, [
    controlledResetSearch,
    setSelectedColumn,
    setKeywordInput,
    setTableData,
    setTotalCount,
    setCurrentPage,
    setHasSearched,
    tableData.rows,
  ]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Enter" && keywordInput && selectedColumn && !loading) {
        handleSearch();
      }
    },
    [keywordInput, selectedColumn, loading, handleSearch]
  );

  return (
    <SearchContainer>
      <MDTypography variant="h6" color="text" sx={{ mb: 1 }}>
        Search Table
      </MDTypography>
      <SearchColumn>
        <StyledFormControl>
          <Select
            labelId="column-select-label"
            value={selectedColumn}
            onChange={(e) => setSelectedColumn(e.target.value)}
            displayEmpty
            renderValue={(selected: string) => {
              const selectedItem = tableData.columns.find((column) => column.accessor === selected);
              return selectedItem ? (
                selectedItem.Header
              ) : (
                <em style={{ fontSize: "0.875rem", fontStyle: "normal" }}>Select a Column</em>
              );
            }}
            inputProps={{ "aria-label": "Select column" }}
            sx={{
              width: "100%",
              height: "36px",
              borderRadius: "8px",
              boxSizing: "border-box",
              color: colors.text.main,
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderWidth: "1px",
                  borderColor: colors.grey[300],
                },
                "&:hover fieldset": {
                  borderColor: colors.grey[500],
                },
                "&.Mui-focused fieldset": {
                  borderColor: colors.primary.main,
                },
              },
              "& .MuiInputBase-input": {
                padding: "8px",
                fontSize: "0.875rem",
                color: colors.text.main,
                boxSizing: "border-box",
                height: "36px",
                display: "flex",
                alignItems: "center",
                textAlign: "center",
              },
              "& .MuiSelect-icon": {
                color: colors.text.main,
              },
            }}
            size="small"
          >
            {tableData.columns.map((column) => (
              <MenuItem
                key={column.accessor}
                value={column.accessor}
                sx={{ fontSize: "0.875rem", justifyContent: "center" }}
              >
                {column.Header}
              </MenuItem>
            ))}
          </Select>
        </StyledFormControl>

        <TextField
          label="Keyword"
          value={keywordInput}
          onChange={(e) => setKeywordInput(e.target.value)}
          onKeyDown={handleKeyDown}
          variant="outlined"
          required
          size="small"
          inputProps={{
            style: { color: colors.primary.main },
          }}
        />
        <ButtonContainer>
          <MDButton
            variant="background"
            color="primary"
            onClick={handleSearch}
            disabled={loading || !selectedColumn || !keywordInput}
            sx={{ height: "40px", flex: "1", padding: "0 16px", borderRadius: "8px" }}
          >
            {loading ? "Searching..." : "Search"}
          </MDButton>
          <MDButton color="default" onClick={handleReset} sx={{ height: "40px", flex: "1", padding: "0 16px", ml: 1 }}>
            Reset
          </MDButton>
        </ButtonContainer>
      </SearchColumn>
      {error && (
        <MDTypography variant="caption" color="error" sx={{ mt: 1 }}>
          Error: {error.message}
        </MDTypography>
      )}
    </SearchContainer>
  );
};

const SearchContainer = styled("div")`
  margin-bottom: 1rem;
  padding: 1rem;
  background: ${colors.background.default};
  border-radius: 8px;
  width: 100%;
  max-width: 400px;
`;

const SearchColumn = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const StyledFormControl = styled(FormControl)`
  width: 100%;
`;

const ButtonContainer = styled("div")`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

export default KeywordSearch;
